import { Cart, CustomLineItem, Order } from '@commercetools/platform-sdk';
import { dollars, from } from '@nuts/auto-delivery-sdk/dist/utils/money';

import { ShippingOffer } from '@/api/shippingCalculator';
import { CartLineItemFields, NutsLineItem } from '@/lib/cart/lineItem';
import { ShopPayDeliveryMethods, ShopPayPaymentRequest } from '@/types/shopify';
import { getTotalDiscountAmount } from '@/utils/cart';
import { Money } from '@/utils/money';

export function getTotalWithoutShipping(cart: Cart | Order | undefined) {
  return Money.subtract(
    cart?.totalPrice ?? from(0),
    cart?.taxedShippingPrice?.totalGross ?? from(0),
  );
}

export function buildShippingLinesPaymentRequest(
  selectedDeliveryMethod: ShopPayDeliveryMethods | undefined,
  currencyCode: string,
) {
  if (!selectedDeliveryMethod) {
    return {
      shippingLines: [],
      totalShippingPrice: undefined,
    };
  }
  return {
    shippingLines: [
      {
        label: selectedDeliveryMethod.label,
        amount: selectedDeliveryMethod.amount,
        code: selectedDeliveryMethod.code,
      },
    ],
    totalShippingPrice: {
      finalTotal: {
        amount: selectedDeliveryMethod.amount.amount,
        currencyCode,
      },
    },
  };
}

export function convertOfferToDeliveryMethod(
  offer: ShippingOffer | undefined,
  hrpItems?: CustomLineItem[],
): ShopPayDeliveryMethods | undefined {
  if (!offer) return undefined;

  const hrpTotal = Money.sumBy(hrpItems, (item) => item.totalPrice);

  return {
    label: offer.name,
    amount: {
      amount: dollars(Money.sum([offer.price, hrpTotal])),
      currencyCode: offer.price.currencyCode,
    },
    code: `${offer.carrier}:${offer.carrierCode}`,
    minDeliveryDate: offer.earliestArrivalOn,
    maxDeliveryDate: offer.latestArrivalOn,
  };
}

export function buildPaymentRequest(
  cart: Cart | Order | undefined,
  lineItems?: NutsLineItem<CartLineItemFields>[],
  shippingOffers?: ShippingOffer[],
  initial = false,
) {
  const currencyCode = cart?.totalPrice.currencyCode ?? 'USD';
  const totalWithoutShipping = getTotalWithoutShipping(cart);
  const cartInfo: ShopPayPaymentRequest = {
    lineItems: [],
    shippingLines: [],
    deliveryMethods: [],
    subtotal: {
      amount: dollars(totalWithoutShipping ?? from(0)),
      currencyCode,
    },
    totalTax: {
      amount: !initial ? dollars(cart?.taxedPrice?.totalTax ?? from(0)) : 0,
      currencyCode,
    },
    total: {
      amount: dollars(
        initial
          ? totalWithoutShipping
          : cart?.taxedPrice?.totalGross ?? cart?.totalPrice ?? from(0),
      ),
      currencyCode,
    },
    presentmentCurrency: currencyCode,
    locale: 'en',
  };

  lineItems?.forEach((lineItem) => {
    const itemDiscountAmount = dollars(lineItem.price.discounted?.value ?? from(0));
    const lineDiscountAmount = dollars(getTotalDiscountAmount(lineItem));
    const image = lineItem.titleImage?.medium;
    cartInfo.lineItems.push({
      label: lineItem.name.en,
      originalItemPrice: {
        amount: dollars(lineItem.price.value),
        currencyCode,
      },
      image: image
        ? {
            url: image,
          }
        : undefined,
      itemDiscounts:
        itemDiscountAmount > 0
          ? [
              {
                label: '',
                amount: {
                  amount: itemDiscountAmount,
                  currencyCode,
                },
              },
            ]
          : undefined,
      finalItemPrice: {
        amount: dollars(Money.divide(lineItem.totalPrice, lineItem.quantity)),
        currencyCode,
      },
      quantity: lineItem.quantity,
      sku: lineItem.variant.sku,
      requiresShipping: lineItem.variant.attributes?.find((a) => a.name === 'weight')?.value > 0,
      originalLinePrice: {
        amount: dollars(Money.multiply(lineItem.price.value, lineItem.quantity)),
        currencyCode,
      },
      lineDiscounts:
        lineDiscountAmount > 0
          ? [
              {
                label: '',
                amount: {
                  amount: lineDiscountAmount,
                  currencyCode,
                },
              },
            ]
          : undefined,
      finalLinePrice: {
        amount: dollars(lineItem.totalPrice),
        currencyCode,
      },
    });
  });

  const hrpItems = cart?.customLineItems.filter((item) =>
    item.slug.endsWith('-heat-resistant-packaging'),
  );

  shippingOffers?.forEach((offer) => {
    const deliveryMethod = convertOfferToDeliveryMethod(offer, hrpItems);
    if (deliveryMethod) {
      cartInfo.deliveryMethods.push(deliveryMethod);
    }
  });

  return cartInfo;
}

export default {};
